import { Controller } from "@hotwired/stimulus";
import L from "leaflet";
import "leaflet.awesome-markers";

export default class extends Controller {
  static targets = [
    "canvas",
    "coverageType",
    "customPolygon",
    "distanceWrapper",
    "polygonWrapper",
  ];

  static values = {
    hasCoordinates: Boolean,
    lat: Number,
    lng: Number,
    distance: Number,
    coverageType: String,
    polygon: Array,
    mileInMeters: { type: Number, default: 1609.34 },
  };

  connect() {
    this.polygonPoints = this._initialPolygonPoints();
    this.hasStoredPolygon = this.polygonPoints.length > 0;
    this.vertexMarkers = [];
    this._bindDistanceInput();

    setTimeout(() => {
      this._initMap();
      this.coverageTypeChanged();
    }, 0);
  }

  disconnect() {
    if (this.distanceInput && this._onDistanceInput) {
      this.distanceInput.removeEventListener("input", this._onDistanceInput);
    }
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  }

  _bindDistanceInput() {
    if (!this.hasDistanceWrapperTarget) return;

    this.distanceInput = this.distanceWrapperTarget.querySelector('input[data-input-target="input"]');
    if (!this.distanceInput) return;

    this._onDistanceInput = () => this.distanceChanged();
    this.distanceInput.addEventListener("input", this._onDistanceInput);
  }

  _initMap() {
    const ukBounds = L.latLngBounds([49.5, -8.7], [59.5, 1.9]);

    this.map = L.map(this.canvasTarget, {
      zoomControl: false,
      attributionControl: true,
    });

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(this.map);

    L.control.zoom({ position: "topleft" }).addTo(this.map);

    delete L.Icon.Default.prototype._getIconUrl;

    if (this.hasCoordinatesValue && Number.isFinite(this.latValue) && Number.isFinite(this.lngValue)) {
      this.map.setView([this.latValue, this.lngValue], 11);
    } else {
      this.map.fitBounds(ukBounds, { padding: [20, 20] });
    }

    this._onMapClick = (e) => this._selectPointOnMapClick(e);
    setTimeout(() => this.map.invalidateSize(), 0);
  }

  coverageTypeChanged() {
    const coverageType = this._currentCoverageType();

    this._clearShape();
    this._clearMapClick();
    this._renderOfficeMarker();

    if (this.hasDistanceWrapperTarget) {
      this.distanceWrapperTarget.classList.toggle("hidden", coverageType !== "radius");
    }
    if (this.hasPolygonWrapperTarget) {
      this.polygonWrapperTarget.classList.toggle("hidden", coverageType !== "custom");
    }

    if (coverageType === "radius") {
      this._renderCircle();
    } else if (coverageType === "custom") {
      this.map.on("click", this._onMapClick);
      this._renderPolygon();
    }
  }

  distanceChanged() {
    if (this._currentCoverageType() !== "radius") return;

    this._renderCircle();
  }

  _selectPointOnMapClick(e) {
    this._discardStoredPolygon();
    this.polygonPoints.push([e.latlng.lat, e.latlng.lng]);
    this.vertexMarkers.push(
      L.circleMarker([e.latlng.lat, e.latlng.lng], {
        radius: 4,
        color: "#7c3aed",
        fillColor: "#7c3aed",
        fillOpacity: 1,
      }).addTo(this.map),
    );
    if (this.hasCustomPolygonTarget) {
      this.customPolygonTarget.value = JSON.stringify(this.polygonPoints);
    }
    this._renderPolygon();
  }

  _renderOfficeMarker() {
    if (this.marker) this.marker.remove();
    if (!this.hasCoordinatesValue) return;
    if (!Number.isFinite(this.latValue) || !Number.isFinite(this.lngValue)) return;

    this.marker = L.marker([this.latValue, this.lngValue], { icon: this._officeIcon() }).addTo(this.map);
  }

  _officeIcon() {
    return L.AwesomeMarkers.icon({ icon: "building", markerColor: "purple", prefix: "fa" });
  }

  _renderCircle() {
    if (this.circle) this.circle.remove();

    const distance = this._currentDistance();
    if (!this.hasCoordinatesValue) return;
    if (!distance || !Number.isFinite(this.latValue) || !Number.isFinite(this.lngValue)) return;

    this.circle = L.circle([this.latValue, this.lngValue], {
      color: "#7c3aed",
      fillColor: "#7c3aed",
      fillOpacity: 0.15,
      radius: distance * this.mileInMetersValue,
    }).addTo(this.map);

    this.map.fitBounds(this.circle.getBounds(), { padding: [40, 40], maxZoom: 13 });
  }

  _renderPolygon() {
    if (this.polygon) this.polygon.remove();
    if (this.polygonPoints.length < 3) return;

    this.polygon = L.polygon(this.polygonPoints, {
      color: "#7c3aed",
      fillColor: "#7c3aed",
      fillOpacity: 0.15,
    }).addTo(this.map);

    this.map.fitBounds(this.polygon.getBounds(), { padding: [40, 40], maxZoom: 13 });
  }

  _discardStoredPolygon() {
    if (!this.hasStoredPolygon) return;

    this.hasStoredPolygon = false;
    this.polygonPoints = [];
    this._clearShape();
  }

  _clearShape() {
    this.vertexMarkers.forEach((marker) => marker.remove());
    this.vertexMarkers = [];

    if (this.circle) {
      this.circle.remove();
      this.circle = null;
    }
    if (this.polygon) {
      this.polygon.remove();
      this.polygon = null;
    }
  }

  _clearMapClick() {
    if (this.map && this._onMapClick) this.map.off("click", this._onMapClick);
  }

  _currentCoverageType() {
    if (this.hasCoverageTypeTarget && this.coverageTypeTarget.value) {
      return this.coverageTypeTarget.value;
    }
    return this.coverageTypeValue;
  }

  _currentDistance() {
    if (this.distanceInput && this.distanceInput.value) {
      return parseFloat(this.distanceInput.value);
    }
    return this.distanceValue;
  }

  _initialPolygonPoints() {
    return this.polygonValue || [];
  }
}
